// Always-visible floating Translate button (right side, mid-screen).
// Bounces gently every ~6s to remind users it's there — useful after they've
// just clicked through to a foreign-language agency website.
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Languages, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function FloatingTranslate() {
  const { user } = useAuth();
  const location = useLocation();
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  // Honor a per-session dismissal but re-show after a tab reload
  useEffect(() => {
    setDismissedThisSession(sessionStorage.getItem("osrr-translate-fab-dismissed") === "1");
  }, []);

  // Hide on the Translation Hub itself (already there) and on public auth pages
  const hidePaths = ["/translate", "/login", "/register"];
  const onHiddenPath = hidePaths.some((p) => location.pathname.startsWith(p));

  // Only show to paid members (also matches the "after they go to a flag" flow,
  // since flags are only browsable post-login)
  const visible = !!user?.has_complete_access && !onHiddenPath && !dismissedThisSession;

  if (!visible) return null;

  return (
    <div
      className="fixed z-[60] right-4 top-1/2 -translate-y-1/2 flex flex-col items-end gap-2 pointer-events-none"
      data-testid="floating-translate"
    >
      <Link
        to="/translate"
        className="osrr-fab pointer-events-auto group flex flex-col items-center gap-1 px-3 py-3 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C7222] text-[#0A1128] shadow-[0_20px_50px_-10px_rgba(212,175,55,0.55)] hover:shadow-[0_25px_60px_-10px_rgba(212,175,55,0.75)] transition-shadow"
        data-testid="floating-translate-btn"
        title="Translate any agency page · any dialect to any dialect"
        aria-label="Open Translation Hub"
      >
        <Languages className="w-6 h-6" strokeWidth={2.4} />
        <span className="text-[9px] uppercase tracking-[0.18em] font-bold leading-none">
          Translate
        </span>
      </Link>

      <button
        type="button"
        onClick={() => {
          sessionStorage.setItem("osrr-translate-fab-dismissed", "1");
          setDismissedThisSession(true);
        }}
        className="pointer-events-auto w-6 h-6 rounded-full bg-black/70 hover:bg-black border border-white/15 text-slate-300 hover:text-white flex items-center justify-center transition-colors"
        data-testid="floating-translate-dismiss"
        title="Hide for this session"
        aria-label="Dismiss translate button for this session"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
